import type { LevelGeometry } from '../level.ts';
import type { Arena } from '../arena.ts';
import { CATHEDRAL_GEOMETRY, makeCathedral } from './cathedral.ts';
import {
  CASTLE_COURTYARD_GEOMETRY, MACHIRO_BAZAAR_GEOMETRY,
  makeCastleCourtyard, makeMachiroBazaar,
} from './graybox.ts';
import { GREAT_CATHEDRAL_GEOMETRY, makeGreatCathedral } from './greatCathedral.ts';

/**
 * The arenas that can be played, and the one played by default.
 *
 * Two entries with different jobs. `cathedral` is the hand-written gray-box: it
 * is what nearly every test builds its world from, so its coordinates are load
 * bearing in a way an *arena* normally is not, and it should not be edited to
 * chase the look of a level. `great-cathedral` is generated from the Godot
 * blockout and is the one that will actually ship.
 *
 * Keeping both is deliberate. Regenerating a level from a .tscn would otherwise
 * move the ground under several hundred assertions that only ever wanted "a
 * room with some walls in it", and the resulting churn would make every level
 * edit look like a gameplay change.
 */
export interface ArenaEntry {
  id: string;
  name: string;
  geometry: LevelGeometry;
  make: () => Arena;
}

export const ARENAS: readonly ArenaEntry[] = [
  {
    id: 'great-cathedral',
    name: 'Great Cathedral',
    geometry: GREAT_CATHEDRAL_GEOMETRY,
    make: makeGreatCathedral,
  },
  /*
   * Two gray-boxes, so a map pool has somewhere to rotate to.
   *
   * Placeholder geometry with real *shapes*: the Courtyard is open ground around
   * a keep and the Bazaar is a grid of stalls with no long sightline in it, so a
   * rotation between the three asks a player three different questions rather
   * than showing them the same fight in a different colour. Both are meant to be
   * replaced by authored levels.
   */
  {
    id: 'castle-courtyard',
    name: 'Castle Courtyard',
    geometry: CASTLE_COURTYARD_GEOMETRY,
    make: makeCastleCourtyard,
  },
  {
    id: 'machiro-bazaar',
    name: 'Machiro Bazaar',
    geometry: MACHIRO_BAZAAR_GEOMETRY,
    make: makeMachiroBazaar,
  },
  {
    id: 'cathedral',
    name: 'Cathedral (gray-box)',
    geometry: CATHEDRAL_GEOMETRY,
    make: makeCathedral,
  },
];

export const DEFAULT_ARENA = ARENAS[0]!;

/** Falls back to the default rather than throwing — a bad id is not fatal. */
export function arenaById(id: string | null | undefined): ArenaEntry {
  return ARENAS.find((a) => a.id === id) ?? DEFAULT_ARENA;
}
